import React, { ChangeEvent, Component, useContext, useEffect, useState } from "react";
import ICreateClientDTO from "../../../models/DTO/ICreateClientDTO";
import IOnCreateClientDTO from "../../../models/DTO/IOnCreateClientDTO";
import IOnUpdateClientDTO from "../../../models/DTO/IOnUpdateClientDTO";
import IUpdateClientDTO from "../../../models/DTO/IUpdateClientDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IClientUI from "../../../models/IClientUI";
import INewAddress from "../../../models/INewAddress";
import ISupplierModel from "../../../models/ISupplierModel";
import IUserUI from "../../../models/IUserUI";
import IVendorModel from "../../../models/IVendorModel";
import SystemConst from "../../../services/SystemConst";
import AddressBlock from "../../shared/AddressBlock/AddressBlock";
import OwButton from "../../shared/OwButton/OwButton";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import OwSelectOption from "../../shared/OwSelectOption/OwSelectOption";
import OwTitle from "../../shared/OwTitle/OwTitle";
import { Socket } from "socket.io-client";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import IClient from "../../../models/IClient";
import { SocketContext } from "../../../contexts/SocketContext";
import UserContext from "../../../contexts/UserContext";
import SupplierContext from "../../../contexts/SupplierContext";
import VendorContext from "../../../contexts/VendorContext";

type Props = {
    client?: IClientUI;
    cancelFn: (clientCreated?: IClient) => void;
    vendorModel?: IVendorModel;
};

export const ClientSidebar = (props: Props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);
    const vendorModel = useContext(VendorContext);
    const socket = useContext(SocketContext)!;

    const [client, setClient] = useState<IClientUI>(
        props.client || ({ Ship_To: {} as INewAddress, Bill_To: {} as INewAddress } as IClientUI)
    );

    const onClientUpdate = (data: IOnUpdateClientDTO) => {
        props.cancelFn(data.client);
    };

    const onClientCreate = (data: IOnCreateClientDTO) => {
        props.cancelFn(data.client);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.ClientOnCreateClient, fn: onClientCreate },
        { endpoint: EndpointsEnum.ClientOnUpdateClient, fn: onClientUpdate },
    ];

    useEffect(() => {
        const _client = props.client;

        if (_client) {
            if (_client.Ship_To && (!_client.Ship_To?.name || !_client.Ship_To?.name.length)) {
                _client.Ship_To.name = _client.name;
            }

            if (_client.Bill_To && (!_client.Bill_To?.name || !_client.Bill_To?.name.length)) {
                _client.Bill_To.name = _client.name;
            }

            setClient(_client);
        }

        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    const cancel = () => {
        props.cancelFn();
    };

    const save = () => {
        if (props.client) {
            const payload: IUpdateClientDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                clientGuid: props.client.guid,
                vendorGuid: props.vendorModel?.guid || vendorModel.guid,
                supplierGuid: supplierModel.guid,
                name: client.name,
                email: client.email,
                Ship_To: client.Ship_To!,
                Bill_To: client.Bill_To!,
            };

            socket.emit(EndpointsEnum.ClientUpdateClient, payload);
        } else {
            const payload: ICreateClientDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                vendorGuid: props.vendorModel?.guid || vendorModel.guid,
                supplierGuid: supplierModel.guid,
                name: client.name,
                email: client.email,
                Ship_To: client.Ship_To || ({} as INewAddress),
                Bill_To: client.Bill_To || ({} as INewAddress),
            };

            socket.emit(EndpointsEnum.ClientCreateClient, payload);
        }
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        const _client = {
            ...client,
            [name]: value,
        };

        setClient(_client);
    };

    const handleShipToChange = (e: any) => {
        const name = e.target.name;
        const value = e.target.value;

        const _client = {
            ...client,
            Ship_To: {
                ...client.Ship_To!,
                [name]: value,
            },
        };

        setClient(_client);
    };

    const handleBillToChange = (e: ChangeEvent<any>) => {
        const name = e.target.name;
        const value = e.target.value;

        const _client = {
            ...client,
            Bill_To: {
                ...client.Bill_To!,
                [name]: value,
            },
        };

        setClient(_client);
    };

    const copyShipTo = () => {
        if (client) {
            let billTo = client.Bill_To!;
            const shipTo = Object.assign({}, client.Ship_To);

            shipTo.id = billTo.id;
            shipTo.guid = billTo.guid;

            billTo = {
                ...shipTo,
            };

            const _client = {
                ...client,
                Bill_To: billTo,
            };

            client.Bill_To = billTo;

            setClient(_client);
        }
    };

    return (
        <div className="ClientSidebar vh-100 p-2 d-flex flex-column">
            <div>
                <div className="text-end">
                    <OwButton className={"btn-danger"} fn={cancel} centerIconClass={"far fa-times"} />
                </div>
                <OwTitle className={"mb-2"} leftIconName={"fad fa-user"} title={"client"} />
                <hr />
            </div>
            <div className={"pe-2"} style={{ flex: 1, overflowY: "auto" }}>
                {props.vendorModel ? (
                    <div>
                        <OwLabel title={"vendor"} supplierModel={supplierModel} isSupplierVendorOverride={true} />
                        <div>{props.vendorModel ? props.vendorModel.name : ""}</div>
                    </div>
                ) : null}
                <div className={"mt-4"}>
                    <OwLabel title={"name"} />
                    <OwInputWrapper name={"name"} fn={handleChange} value={client.name} />
                </div>
                <div className={"mt-4"}>
                    <OwLabel title={"email"} />
                    <OwInputWrapper name={"email"} fn={handleChange} value={client.email} />
                </div>
                <div>
                    <div className={"mt-4 mb-2"}>
                        <OwLabel title={"ship_to"} />
                        <AddressBlock address={client.Ship_To} fn={handleShipToChange} showName={true} />
                    </div>

                    <div className={"mt-4 mb-2"}>
                        <div className={"mb-2"}>
                            <OwLabel title={"bill_to"} className={"me-2"} />
                            <OwButton title={"copy_ship_to"} fn={copyShipTo} />
                        </div>
                        <AddressBlock address={client.Bill_To} fn={handleBillToChange} showName={true} />
                    </div>
                </div>
            </div>
            <div className={"p-2"}>
                <OwButton fn={save} title={"save"} />
                <OwButton className={"ms-2 btn-danger"} fn={cancel} title={"cancel"} />
            </div>
        </div>
    );
};

export default ClientSidebar;
